
import React, { useState, useEffect, useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { FaEdit, FaTrash } from 'react-icons/fa';
import config from "../../../config/config";
import { getAuthToken } from "../../../utils/token";
import axios from "axios";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
//import Loader from '../../loader/Loader';

const initialState = {
  material_name: "",
  material_code: "",
  material_type: "",
  description: "",
  unit_price: "",
  quantity_in_stock: "",
  unit_of_measurement: "",
  is_active: true,
};

export default function Material() {
  const [dataList, setDataList] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [backendErrors, setBackendErrors] = useState({});
  const [units, setUnits] = useState([]);
  const [form, setForm] = useState(initialState);

  useEffect(() => {
    fetchUnits();
    fetchList();
  }, []);

  // Fetch units
  const fetchUnits = async () => {
    try {
      const res = await axios.post(
        `${config.API}/getUnit`,
        {},
        {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        }
      );

      setUnits(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error fetching units:", error);
      toast.error("Failed to load units");
    }
  };

  // Fetch materials
  const fetchList = async () => {
    const token = getAuthToken();

    if (!token) {
      toast.error("Authentication missing");
      return;
    }

    setListLoading(true);

    try {
      const res = await axios.get(
        `${config.API}/material/master/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setDataList(res.data.materials || []);
    } catch (error) {
      console.error("Error fetching material list:", error);
      toast.error("Failed to load materials");
    } finally {
      setListLoading(false);
    }
  };

  // Handle form inputs
  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setBackendErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };

  // Open add modal
  const openAddModal = () => {
    setForm({ ...initialState });
    setBackendErrors({});
    setIsEditing(false);
    setEditingId(null);
    setShowModal(true);
  };

  // Open edit modal
  const openEditModal = (item) => {
    setForm({
      ...initialState,
      ...item,
      unit_price: item.unit_price ?? "",
      quantity_in_stock: item.quantity_in_stock ?? "",
      is_active:
        item.is_active === true ||
        item.is_active === 1 ||
        item.is_active === "1",
    });

    setBackendErrors({});
    setIsEditing(true);
    setEditingId(item.id);
    setShowModal(true);
  };

  // Save or update material
  const handleSubmit = async () => {
    const token = getAuthToken();

    if (!token) {
      toast.error("Authentication missing");
      return;
    }

    const payload = {
      material_name: form.material_name,
      material_code: form.material_code,
      material_type: form.material_type,
      description: form.description,
      unit_price: Number(form.unit_price || 0),
      quantity_in_stock: Number(form.quantity_in_stock || 0),
      unit_of_measurement: form.unit_of_measurement,
      is_active: Boolean(form.is_active),
    };

    setFormLoading(true);
    setBackendErrors({});

    try {
      let response;

      if (isEditing && editingId) {
        response = await axios.put(
          `${config.API}/material/master/${editingId}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        response = await axios.post(
          `${config.API}/material/master`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      if (response?.data?.errors) {
        setBackendErrors(response.data.errors);
        return;
      }

      toast.success(
        response?.data?.message ||
          (isEditing
            ? "Material updated successfully"
            : "Material created successfully")
      );

      setShowModal(false);
      await fetchList();
    } catch (error) {
      const validationErrors = error?.response?.data?.errors;

      if (validationErrors) {
        setBackendErrors(validationErrors);
      } else {
        toast.error(
          error?.response?.data?.message ||
            "Unable to save material"
        );
      }
    } finally {
      setFormLoading(false);
    }
  };

  // Instant search across material fields
  const filtered = useMemo(() => {
    const search = filterText.trim().toLowerCase();

    if (!search) return dataList;

    return dataList.filter((item) =>
      [
        item.id,
        item.material_name,
        item.material_code,
        item.material_type,
        item.description,
        item.unit_price,
        item.quantity_in_stock,
        item.unit_of_measurement,
        item.is_active ? "yes active" : "no inactive",
      ]
        .map((value) => String(value ?? ""))
        .join(" ")
        .toLowerCase()
        .includes(search)
    );
  }, [dataList, filterText]);

  // MUI DataGrid columns
  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 75,
        type: "number",
      },
      {
        field: "material_name",
        headerName: "Material Name",
        minWidth: 250,
        flex: 1,
      },
      {
        field: "material_code",
        headerName: "Code",
        minWidth: 110,
        flex: 0.7,
      },
      {
        field: "material_type",
        headerName: "Type",
        minWidth: 110,
        flex: 0.7,
      },
      {
        field: "unit_of_measurement",
        headerName: "Unit",
        width: 90,
      },
      {
        field: "is_active",
        headerName: "Active",
        width: 90,
        renderCell: (params) => {
          const active =
            params.value === true ||
            params.value === 1 ||
            params.value === "1";

          return (
            <span
              className={`badge ${
                active ? "bg-success" : "bg-secondary"
              }`}
            >
              {active ? "Yes" : "No"}
            </span>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              height: "100%",
            }}
          >
            <Tooltip title="Edit Material">
              <IconButton
                size="small"
                color="primary"
                onClick={() => openEditModal(params.row)}
              >
                <FaEdit size={15} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete Material">
              <IconButton
                size="small"
                color="error"
                onClick={() =>
                  toast.info(
                    "Connect your material delete API here."
                  )
                }
              >
                <FaTrash size={14} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    []
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Header and search */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box
          sx={{
            fontSize: "1.25rem",
            fontWeight: 700,
            color: "#183153",
          }}
        >
          Material Master
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <TextField
            size="small"
            placeholder="Search materials..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            sx={{
              width: { xs: "100%", sm: 250 },
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
                borderRadius: 2,
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: filterText ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => setFilterText("")}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />

          <button
            type="button"
            className="btn btn-primary text-nowrap"
            onClick={openAddModal}
          >
            + Add Material
          </button>
        </Box>
      </Box>

      {/* DataGrid */}
<Box
  sx={{
    width: "100%",
    backgroundColor: "#fff",
    borderRadius: 2,

    // Column header container
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#0d6efd !important",
      color: "#fff",
      minHeight: "42px !important",
      maxHeight: "42px !important",
      lineHeight: "42px !important",
    },

    // Individual column headers
    "& .MuiDataGrid-columnHeader": {
      backgroundColor: "#0d6efd !important",
      color: "#fff !important",
      minHeight: "42px !important",
      maxHeight: "42px !important",
      outline: "none",
    },

    // Header text
    "& .MuiDataGrid-columnHeaderTitle": {
      color: "#fff !important",
      fontWeight: 700,
      fontSize: "12px",
      visibility: "visible",
      opacity: 1,
    },

    // Header title container
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      visibility: "visible",
    },

    // Grid cells
    "& .MuiDataGrid-cell": {
      fontSize: "12px",
    },

    "& .MuiDataGrid-row:hover": {
      backgroundColor: "#f0f6ff",
    },

    "& .MuiDataGrid-footerContainer": {
      minHeight: 45,
    },

    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
      outline: "none",
    },
  }}
>
  <DataGrid
    rows={filtered}
    columns={columns}
    getRowId={(row) => row.id}
    loading={listLoading}
    autoHeight
    density="compact"
    disableRowSelectionOnClick
    pageSizeOptions={[10, 25, 50, 100]}
    initialState={{
      pagination: {
        paginationModel: {
          page: 0,
          pageSize: 10,
        },
      },
    }}
    localeText={{
      noRowsLabel: filterText
        ? "No matching materials found"
        : "No materials available",
    }}
  />
</Box>

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          className="modal d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-primary text-light">
                <h5 className="modal-title">
                  {isEditing ? "Edit Material" : "Add Material"}
                </h5>

                <button
                  type="button"
                  className="btn-close btn-close-white"
                  aria-label="Close"
                  disabled={formLoading}
                  onClick={() => setShowModal(false)}
                />
              </div>

              <div className="modal-body">
                <div className="row g-3">
                  {/* Material Name */}
                  <div className="col-md-4">
                    <label className="form-label">
                      Material Name
                    </label>
                    <input
                      type="text"
                      name="material_name"
                      className="form-control"
                      value={form.material_name}
                      onChange={handleInput}
                      disabled={formLoading}
                    />
                    {backendErrors.material_name && (
                      <div className="text-danger small mt-1">
                        {backendErrors.material_name[0]}
                      </div>
                    )}
                  </div>

                  {/* Material Code */}
                  <div className="col-md-4">
                    <label className="form-label">
                      Material Code
                    </label>
                    <input
                      type="text"
                      name="material_code"
                      className="form-control"
                      value={form.material_code}
                      onChange={handleInput}
                      disabled={formLoading}
                    />
                    {backendErrors.material_code && (
                      <div className="text-danger small mt-1">
                        {backendErrors.material_code[0]}
                      </div>
                    )}
                  </div>

                  {/* Material Type */}
                  <div className="col-md-4">
                    <label className="form-label">
                      Material Type
                    </label>
                    <input
                      type="text"
                      name="material_type"
                      className="form-control"
                      value={form.material_type}
                      onChange={handleInput}
                      disabled={formLoading}
                    />
                    {backendErrors.material_type && (
                      <div className="text-danger small mt-1">
                        {backendErrors.material_type[0]}
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div className="col-md-4">
                    <label className="form-label">
                      Description
                    </label>
                    <input
                      type="text"
                      name="description"
                      className="form-control"
                      value={form.description}
                      onChange={handleInput}
                      disabled={formLoading}
                    />
                    {backendErrors.description && (
                      <div className="text-danger small mt-1">
                        {backendErrors.description[0]}
                      </div>
                    )}
                  </div>

                  {/* Unit Price */}
                  <div className="col-md-4">
                    <label className="form-label">
                      Unit Price
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      name="unit_price"
                      className="form-control"
                      value={form.unit_price}
                      onChange={handleInput}
                      disabled={formLoading}
                    />
                    {backendErrors.unit_price && (
                      <div className="text-danger small mt-1">
                        {backendErrors.unit_price[0]}
                      </div>
                    )}
                  </div>

                  {/* Quantity */}
                  <div className="col-md-4">
                    <label className="form-label">
                      Quantity in Stock
                    </label>
                    <input
                      type="number"
                      min="0"
                      name="quantity_in_stock"
                      className="form-control"
                      value={form.quantity_in_stock}
                      onChange={handleInput}
                      disabled={formLoading}
                    />
                    {backendErrors.quantity_in_stock && (
                      <div className="text-danger small mt-1">
                        {backendErrors.quantity_in_stock[0]}
                      </div>
                    )}
                  </div>

                  {/* Unit */}
                  <div className="col-md-4">
                    <label className="form-label">
                      Unit of Measurement
                    </label>
                    <select
                      name="unit_of_measurement"
                      className="form-select"
                      value={form.unit_of_measurement}
                      onChange={handleInput}
                      disabled={formLoading}
                    >
                      <option value="">Select Unit</option>
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.name}>
                          {unit.name}
                        </option>
                      ))}
                    </select>
                    {backendErrors.unit_of_measurement && (
                      <div className="text-danger small mt-1">
                        {backendErrors.unit_of_measurement[0]}
                      </div>
                    )}
                  </div>

                  {/* Active */}
                  <div className="col-md-4 d-flex align-items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      name="is_active"
                      className="form-check-input me-2"
                      checked={Boolean(form.is_active)}
                      onChange={handleInput}
                      disabled={formLoading}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="is_active"
                    >
                      Active
                    </label>
                  </div>

                  {backendErrors.is_active && (
                    <div className="col-12 text-danger small">
                      {backendErrors.is_active[0]}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={formLoading}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={formLoading}
                >
                  {formLoading
                    ? "Saving..."
                    : isEditing
                    ? "Update"
                    : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
}
